import {
  AbstractControl,
  FormArray,
  FormGroup,
  ValidationErrors
} from '@angular/forms';

export function toMinutes (time: string): number | null {
  if (!time || !(/^\d{2}:\d{2}$/).test(time)) return null;

  const parts: number[] = time.split(':').map(Number);
  const hours: number | undefined = parts[ 0 ];
  const minutes: number | undefined = parts[ 1 ];

  if (
    Number.isNaN(hours) || Number.isNaN(minutes)
    || hours === undefined || minutes === undefined
    || hours < 0 || hours > 24 || minutes < 0 || minutes > 59
  ) return null;

  if (hours === 24 && minutes !== 0) return null;

  return hours * 60 + minutes;
}
export function normalizeTimeInput (raw: unknown): string | null {
  if (raw === null || raw === undefined) return null;

  let rawValue: string = String(raw).trim();

  if (!rawValue) return null;

  rawValue = rawValue.replaceAll(/[^\d:]/g, '');

  if (!rawValue.includes(':')) {
    rawValue = rawValue.length <= 2
      ? `${ rawValue.padStart(2, '0') }:00`
      : `${ rawValue.slice(0, -2).padStart(2, '0') }:${ rawValue.slice(-2).padEnd(2, '0') }`;
  }

  const [ hoursRaw = '0', minutesRaw = '00' ]: string[] = rawValue.split(':');

  let hours: number = Number.parseInt(hoursRaw, 10) || 0;
  let minutes: number = Number.parseInt(minutesRaw, 10) || 0;

  if (hours < 0) hours = 0;

  if (hours > 24) hours = 24;

  if (hours === 24) minutes = 0;

  minutes = Math.min(59, Math.max(0, minutes));

  const formatted: string = `${ hours.toString().padStart(2, '0') }:${ minutes
    .toString()
    .padStart(2, '0') }`;

  return formatted;
}
export function getTimeRangesFromFormArray (rangesFa: FormArray<FormGroup>): TimeRange[] {
  return rangesFa.controls
    .map((timeRangeGroup: FormGroup): TimeRange => ({
      from: String(timeRangeGroup.get('from')?.value ?? ''),
      to: String(timeRangeGroup.get('to')?.value ?? '')
    }))
    .filter((range: TimeRange) => range.from && range.to);
}
export function setErr (ctrl: AbstractControl, key: string, val: boolean = true): void {
  ctrl.setErrors({ ...ctrl.errors, [ key ]: val });
}
export function delErr (ctrl: AbstractControl, key: string): void {
  const errors: ValidationErrors = ctrl.errors ?? {};

  if (!(key in errors)) return;

  const { [ key ]: _, ...rest }: ValidationErrors = errors;

  ctrl.setErrors(Object.keys(rest).length ? rest : null);
}
export function clearOverlapErrors (rangesFa: FormArray<FormGroup>): void {
  for (const timeRangeGroup of rangesFa.controls) {
    delErr(timeRangeGroup, 'overlap');

    const fromCtrl: AbstractControl | null = timeRangeGroup.get('from');

    if (fromCtrl) delErr(fromCtrl, 'overlap');

    const toCtrl: AbstractControl | null = timeRangeGroup.get('to');

    if (toCtrl) delErr(toCtrl, 'overlap');
  }
}
export function markOverlaps (rangesFa: FormArray<FormGroup>): boolean {
  clearOverlapErrors(rangesFa);

  type Slot = { from: number; to: number; ctrl: FormGroup; };

  const slots: Slot[] = [];

  for (const rangeGroup of rangesFa.controls) {
    const from: number | null = toMinutes(rangeGroup.get('from')?.value as string);
    const to: number | null = toMinutes(rangeGroup.get('to')?.value as string);

    if (from === null || to === null || from >= to) continue;

    slots.push({ from, to, ctrl: rangeGroup });
  }

  if (slots.length < 2) return false;

  slots.sort((slotA: Slot, slotB: Slot) => slotA.from - slotB.from);

  let hasOverlaps: boolean = false;
  let previousSlot: Slot | undefined = slots[ 0 ];

  for (let i: number = 1; i < slots.length; i++) {
    const currentSlot: Slot | undefined = slots[ i ];

    if (currentSlot && previousSlot && currentSlot.from < previousSlot.to) {
      hasOverlaps = true;

      for (const overlappingSlot of [ previousSlot, currentSlot ]) {
        setErr(overlappingSlot.ctrl, 'overlap');

        const fromCtrl: AbstractControl | null = overlappingSlot.ctrl.get('from');
        const toCtrl: AbstractControl | null = overlappingSlot.ctrl.get('to');

        if (fromCtrl) setErr(fromCtrl, 'overlap');

        if (toCtrl) setErr(toCtrl, 'overlap');
      }

      if (currentSlot.to > previousSlot.to) previousSlot = currentSlot;
    } else
      previousSlot = currentSlot;
  }

  return hasOverlaps;
}
export interface TimeRange {
  from: string;
  to: string;
}
export function isTimeDisabledInRanges (
  time: string,
  existingRanges: TimeRange[],
  isFrom: boolean = true,
  currentRangeIndex?: number
): boolean {
  const minutes: number | null = toMinutes(time);

  if (minutes === null) return false;

  if (minutes === 24 * 60) {
    const otherHas24To: boolean = existingRanges.some((range: TimeRange, index: number) => {
      if (currentRangeIndex !== undefined && index === currentRangeIndex) return false;

      const toMinutesValue: number | null = toMinutes(range.to);

      return toMinutesValue === 24 * 60;
    });

    if (otherHas24To) return true;
  }

  return existingRanges.some((range: TimeRange, index: number): boolean => {
    if (currentRangeIndex !== undefined && index === currentRangeIndex) return false;

    const fromMinutes: number | null = toMinutes(range.from);
    const toMinutesValue: number | null = toMinutes(range.to);

    if (fromMinutes === null || toMinutesValue === null) return false;

    return isFrom
      ? minutes >= fromMinutes && minutes < toMinutesValue
      : minutes > fromMinutes && minutes <= toMinutesValue;
  });
}
export function isToOptionDisabledInRanges (
  optionTime: string,
  existingRanges: TimeRange[],
  currentRangeIndex: number,
  currentFromMinutes: number | null
): boolean {
  const toOptionMinutes: number | null = toMinutes(optionTime);

  if (toOptionMinutes === null) return false;

  if (currentFromMinutes === null) return true;

  if (toOptionMinutes <= currentFromMinutes) return true;

  for (let i: number = 0; i < existingRanges.length; i++) {
    const existingRange: TimeRange | undefined = existingRanges[ i ];

    if (!existingRange || i === currentRangeIndex) continue;

    if (!existingRange.from || !existingRange.to) continue;

    const otherFromMinutes: number | null = toMinutes(existingRange.from);
    const otherToMinutes: number | null = toMinutes(existingRange.to);

    if (otherFromMinutes === null || otherToMinutes === null) continue;

    const overlaps: boolean
      = currentFromMinutes < otherToMinutes && toOptionMinutes > otherFromMinutes;

    if (overlaps) return true;
  }

  return false;
}

